/**
 * System-UI adapter — drives the NATIVE Android system-navigation-bar icon
 * appearance from the app's light/dark theme.
 *
 * Why this exists. The Android OS draws the bottom system-navigation bar
 * (◀ ● ■) with its own icon colour, which does NOT follow the web app's theme.
 * By default those icons render LIGHT (white), so in the app's LIGHT theme they
 * wash out against the app's light bottom chrome (white-on-white) — on the home
 * screen and on every layover surface. The fix is to make the OS draw DARK
 * nav-bar icons in light mode and LIGHT icons in dark mode, so they always
 * contrast — app-wide, with no dark masking strip.
 *
 * Mechanism. The native shell embeds a tiny Capacitor plugin (`NavigationBar`,
 * in apps/mobile/.../MainActivity.java) exposing `setAppearance({ light })`,
 * which calls `WindowInsetsControllerCompat.setAppearanceLightNavigationBars()`.
 * `light: true` → dark icons (light app theme); `light: false` → light icons
 * (dark app theme). No new plugin or Gradle dependency:
 * `WindowInsetsControllerCompat` is already on the classpath via androidx.core.
 *
 * Call site. The single theme switch (theme.ts `applyResolvedTheme`, run by
 * ThemeProvider on initial load AND on every theme toggle) calls
 * `applyNativeNavBarAppearance(resolved)`, so the nav-bar icons track the theme
 * at startup and live on toggle.
 *
 * Platform contract. A NO-OP everywhere but native: on web/desktop/iOS it
 * returns immediately WITHOUT importing or invoking the plugin, so nothing
 * Capacitor-shaped leaks into the web bundle's eager graph (the plugin is
 * reached only via a dynamic `import()` behind the native gate). Fail-soft: a
 * missing plugin / native error never throws into the theme effect.
 *
 * Portability (CP1–CP4). Lives in `apps/web/adapters` — the only place
 * platform globals / Capacitor calls belong (§7). `packages/shared` never sees
 * any of this.
 *
 * Testability. `applyNativeNavBarAppearance` takes its platform probe and its
 * plugin loader as injectable options, so the no-op contract, the light/dark
 * mapping, and the fail-soft path are pinned by unit tests with no device and
 * no real plugin. The defaults wire the real probe + the dynamically-registered
 * plugin.
 */

import { isNativePlatform } from '../routes/nativeGoogleSignIn';

/** The effective palette the theme resolved to. Mirrors theme.ts `ResolvedTheme`. */
export type ResolvedTheme = 'light' | 'dark';

/**
 * The minimal slice of the embedded `NavigationBar` Capacitor plugin this
 * adapter calls. `light: true` → dark nav-bar icons (light app theme);
 * `light: false` → light nav-bar icons (dark app theme). Kept as an interface
 * (not the plugin directly) so a fake is injectable in tests.
 */
export interface NavigationBarPlugin {
  setAppearance(options: { light: boolean }): Promise<unknown>;
}

export interface ApplyNavBarAppearanceOptions {
  /**
   * Platform probe. Defaults to the real `isNativePlatform()` (true only inside
   * the Capacitor shell). Tests inject `() => false` (web) or a fake-true to
   * drive the native branch.
   */
  isNative?: () => boolean;
  /**
   * Plugin override. Defaults to the registered `NavigationBar` plugin, reached
   * via a dynamic `import('@capacitor/core')` only on the native branch. Tests
   * inject a fake to assert the light/dark mapping without a device.
   */
  plugin?: NavigationBarPlugin;
  /**
   * Diagnostics for a failed native call. Default no-op. The theme effect must
   * never break, but a broken native bridge should be observable rather than
   * silently swallowed.
   */
  onError?: (error: unknown) => void;
}

/**
 * Apply the resolved theme to the native nav-bar icon appearance.
 *
 * Native: call the embedded `NavigationBar` plugin with `light = (resolved ===
 * 'light')` — dark icons for the light theme, light icons for the dark theme.
 * Web / desktop / iOS: a NO-OP — returns immediately without importing or
 * invoking the plugin. Fail-soft + logged on native.
 */
export async function applyNativeNavBarAppearance(
  resolved: ResolvedTheme,
  options: ApplyNavBarAppearanceOptions = {},
): Promise<void> {
  const isNative = options.isNative ?? isNativePlatform;

  // The platform gate is the whole no-op contract: off native we return before
  // touching the plugin, so web never imports native code for this call.
  if (!isNative()) return;

  try {
    const plugin = options.plugin ?? (await loadNavigationBarPlugin());
    // `light: true` tells Android to draw DARK nav-bar icons (for a LIGHT app
    // background); `false` restores the default light/white icons (for DARK).
    await plugin.setAppearance({ light: resolved === 'light' });
  } catch (error) {
    // A missing plugin (older native build), a bridge error, or no window yet
    // must never throw into the theme effect — but stays observable.
    options.onError?.(error);
  }
}

/**
 * Wrap the raw Capacitor plugin proxy in a plain, NON-THENABLE facade. The proxy
 * `registerPlugin` returns forwards EVERY property access to the native bridge —
 * including `.then`. So if an `async` function RETURNS the proxy (or you `await`
 * it), the Promise machinery probes `.then` on the resolved value and invokes the
 * non-existent native `NavigationBar.then()`, throwing `"NavigationBar.then()" is
 * not implemented on android` — silently killing the nav-bar appearance call. The
 * facade exposes ONLY this seam's interface method and has no `.then`, so it is
 * safe to await/return. The arrow references `raw` LAZILY (only inside the call),
 * so building the facade never touches `raw.then`. Mirrors the secureStorage.ts
 * seam. Exported for the regression test.
 */
export function wrapNavigationBarPlugin(raw: NavigationBarPlugin): NavigationBarPlugin {
  return {
    setAppearance: (options) => raw.setAppearance(options),
  };
}

/**
 * Native-only loader for the embedded `NavigationBar` plugin. Dynamically
 * imported so the web bundle never eagerly pulls `@capacitor/core` for this
 * path; reached only on the native branch above. Returns the NON-THENABLE facade,
 * never the raw proxy (see `wrapNavigationBarPlugin`).
 */
async function loadNavigationBarPlugin(): Promise<NavigationBarPlugin> {
  const { registerPlugin } = await import('@capacitor/core');
  return wrapNavigationBarPlugin(registerPlugin<NavigationBarPlugin>('NavigationBar'));
}
